import { useState } from 'react';

import type { AttachmentI } from 'declarations/interfaces/attachments';
import type { NoopT } from 'declarations/types/common';
import { useDownloadAttachmentFromSignedUrl, useGetAttachmentUrl } from 'hooks/api/useAttachments';
import { useFetchTicketAttachments } from 'hooks/api/useTickets';

import { ActionTypeE } from '../declarations';

type UseViewAttachmentsPopupT = {
  attachments: AttachmentI[] | null;
  isLoadingAttachments: boolean;
  isAttachmentsFetchingError: boolean;
  isDeleteAttachmentPopupOpened: boolean;
  isApproveAttachmentsPopupOpened: boolean;
  handleCloseDeleteAttachmentPopup: NoopT;
  selectedAttachment: AttachmentI | null;
  selectedAttachmentsForApprove: string[] | null;
  isLoadingAttachmentForDownload: boolean;
  isLoadingAttachmentForPreview: boolean;
  isAttachmentForDownloadError: boolean;
  isAttachmentForPreviewError: boolean;
  handleOpenDeleteAttachmentPopup: (attachment: AttachmentI) => NoopT;
  handleApproveAttachClick: (attachmentName: string) => NoopT;
  handleAttachmentClick: (attachment: AttachmentI, actionType: ActionTypeE) => NoopT;
  handleOpenApproveAttachmentsPopup: () => void;
  handleCloseApproveAttachmentsPopup: () => void;
};

type UseViewAttachmentsPopupParamsT = {
  ticketId: string;
};

const useViewAttachmentsPopup = ({
  ticketId,
}: UseViewAttachmentsPopupParamsT): UseViewAttachmentsPopupT => {
  /**
   * Hooks
   */
  const [selectedAttachment, selectAttachment] = useState<null | AttachmentI>(null);
  const [selectedAttachmentsForApprove, selectAttachmentForApprove] = useState<null | string[]>(
    null
  );
  const [actionType, setActionType] = useState<null | ActionTypeE>(null);
  const [isDeleteAttachmentPopupOpened, setDeleteAttachmentPopupVisibility] = useState(false);
  const [isApproveAttachmentsPopupOpened, setApproveAttachmentsPopupVisibility] = useState(false);

  /**
   * Variables
   */
  const shouldFetchAttachments = !!ticketId;
  const shouldViewAttachment = !!selectedAttachment && actionType === ActionTypeE.View;
  const shouldDownloadAttachment = !!selectedAttachment && actionType === ActionTypeE.Download;

  /**
   * Api hooks
   */
  const attachmentsResult = useFetchTicketAttachments({
    ticketId,
    shouldFetch: shouldFetchAttachments,
  });
  const attachmentForDownloadResult = useDownloadAttachmentFromSignedUrl({
    ticketId,
    fileName: selectedAttachment?.fileName || '',
    shouldFetch: shouldDownloadAttachment,
    onSuccess: ({ data, contentType }) => {
      const fileName =
        (selectedAttachment?.originalName as string) || (selectedAttachment?.fileName as string);
      const blob = new Blob([data], { type: contentType });
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = href;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);

      selectAttachment(null);
      setActionType(null);
    },
  });
  const attachmentForPreviewResult = useGetAttachmentUrl({
    ticketId,
    fileName: selectedAttachment?.fileName || '',
    shouldFetch: shouldViewAttachment,
    onSuccess: ({ value }) => {
      window.open(value, '_blank');

      selectAttachment(null);
      setActionType(null);
    },
  });

  /**
   * Handlers
   */
  const handleOpenDeleteAttachmentPopup = (attachment: AttachmentI) => (): void => {
    setDeleteAttachmentPopupVisibility(true);
    selectAttachment(attachment);
  };
  const handleCloseDeleteAttachmentPopup = (): void => {
    setDeleteAttachmentPopupVisibility(false);
    selectAttachment(null);
  };
  const handleAttachmentClick = (attachment: AttachmentI, actionType: ActionTypeE) => (): void => {
    selectAttachment(attachment);
    setActionType(actionType);
  };

  const handleApproveAttachClick = (attachmentName: string) => (): void => {
    selectAttachmentForApprove(prevAttachmentNames => {
      if (prevAttachmentNames && prevAttachmentNames.includes(attachmentName)) {
        return prevAttachmentNames.filter(name => name !== attachmentName);
      } else {
        return [...(prevAttachmentNames || []), attachmentName];
      }
    });
  };

  const handleOpenApproveAttachmentsPopup = (): void => {
    setApproveAttachmentsPopupVisibility(true);
  };
  const handleCloseApproveAttachmentsPopup = (): void => {
    setApproveAttachmentsPopupVisibility(false);
    selectAttachmentForApprove([]);
  };

  return {
    attachments: attachmentsResult.data ?? null,
    isLoadingAttachments: attachmentsResult.isLoading || attachmentsResult.isRefetching,
    isAttachmentsFetchingError: attachmentsResult.isError,
    isDeleteAttachmentPopupOpened,
    isApproveAttachmentsPopupOpened,
    handleOpenDeleteAttachmentPopup,
    handleCloseDeleteAttachmentPopup,
    selectedAttachment,
    selectedAttachmentsForApprove,
    handleAttachmentClick,
    handleApproveAttachClick,
    handleOpenApproveAttachmentsPopup,
    handleCloseApproveAttachmentsPopup,
    isLoadingAttachmentForDownload: attachmentForDownloadResult.isFetching,
    isLoadingAttachmentForPreview: attachmentForPreviewResult.isFetching,
    isAttachmentForDownloadError: attachmentForDownloadResult.isError,
    isAttachmentForPreviewError: attachmentForPreviewResult.isError,
  };
};

export default useViewAttachmentsPopup;
